import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Group, Loader, Paper, Text, Title } from "@mantine/core";
import { useUpdateSegment } from "../api/admin";
import { ApiError } from "../api/client";
import { usePublicSettings, useSegment } from "../api/queries";
import type { Segment } from "../api/types";
import { usePlayer } from "../state/player";
import { msToZonedText, zonedToMs } from "../time/parse";
import { useZone } from "../time/useZone";
import { Chip } from "../ui/Chip";
import { FeedbackLine } from "../ui/FeedbackLine";
import {
  ArtworkPanel,
  ExportedNotice,
  PublishPanel,
  ReviewChips,
  SplitProposal,
  StructureChips,
  type Run,
} from "./InspectorActions";
import { ExcisionsNote, FactsGrid, StateBadge } from "./InspectorFacts";
import {
  MetaFields,
  SegmentTimes,
  editedEdges,
  fieldsFor,
  toForm,
  type Form,
} from "./InspectorForm";
import { useUndo } from "./undo";

/** The editor for whichever block is selected. Nothing here writes without being asked to. */
export function Inspector() {
  const selectedId = usePlayer((s) => s.selectedId);
  const { data: segment } = useSegment(selectedId);
  const { t } = useTranslation("admin");

  if (selectedId === null) {
    return <EmptyInspector>{t("inspector.selectPrompt")}</EmptyInspector>;
  }
  if (segment === null) {
    return <EmptyInspector>{t("inspector.unavailable")}</EmptyInspector>;
  }
  if (!segment) {
    return (
      <EmptyInspector>
        <Loader size="sm" />
      </EmptyInspector>
    );
  }
  return <Editor key={segment.id} segment={segment} />;
}

/**
 * The panel with nothing to edit: the same card, contents centred. The border and the scroll
 * are the panel's own; the grid and the centred ink are what the empty state adds.
 */
function EmptyInspector({ children }: { children: ReactNode }) {
  return (
    <Paper
      component="aside"
      p={14}
      c="dimmed"
      ta="center"
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        display: "grid",
        placeItems: "center",
      }}
    >
      {children}
    </Paper>
  );
}

function Editor({ segment }: { segment: Segment }) {
  const zone = useZone();
  const { t } = useTranslation("admin");
  // The cover a coverless show wears in the list and the player, so the panel that changes
  // covers shows the same thing rather than a gradient the reader sees nowhere else.
  const defaultCover = usePublicSettings().data?.default_cover_url ?? null;
  const update = useUpdateSegment();

  const [error, setError] = useState<string | null>(null);
  const exported = segment.state === "exported";
  const fields = fieldsFor(segment.kind);

  // Keep an edit against the server row it started from. Refetches often return a new object
  // with the same values, so object identity cannot decide whether a draft is still valid.
  const [draft, setDraft] = useState<{ seed: Segment; zone: string; form: Form } | null>(null);
  const activeDraft = draft?.seed.id === segment.id ? draft : null;
  const form = activeDraft?.form ?? toForm(segment, zone.name);
  const setForm = (edit: (current: Form) => Form) =>
    setDraft((held) => {
      const current = held?.seed.id === segment.id ? held : null;
      return {
        seed: current?.seed ?? segment,
        zone: current?.zone ?? zone.name,
        form: edit(current?.form ?? form),
      };
    });

  const startMs = zonedToMs(form.start, zone.name);
  const endMs = zonedToMs(form.end, zone.name);
  const timesValid = startMs !== null && endMs !== null && endMs > startMs;
  const edges = editedEdges(segment, form, zone.name);
  const dirty =
    edges.start !== undefined ||
    edges.end !== undefined ||
    // Only the fields this kind actually shows. A track's hidden `djs` is not the operator's
    // doing and must not count as an unsaved change.
    fields.some((name) => form[name] !== (segment[name] ?? ""));
  const serverChanged =
    activeDraft !== null &&
    (activeDraft.zone !== zone.name ||
      activeDraft.seed.start !== segment.start ||
      activeDraft.seed.end !== segment.end ||
      fields.some((name) => activeDraft.seed[name] !== segment[name]));
  const conflict = serverChanged && dirty;
  const useServer = () => setDraft(null);
  const keepDraft = () => {
    if (!activeDraft) return;
    const base = toForm(activeDraft.seed, activeDraft.zone);
    const rebased = toForm(segment, zone.name);
    for (const name of fieldsFor(activeDraft.seed.kind)) {
      if (activeDraft.form[name] !== base[name]) rebased[name] = activeDraft.form[name];
    }
    for (const edge of ["start", "end"] as const) {
      if (activeDraft.form[edge] === base[edge]) continue;
      const ms = zonedToMs(activeDraft.form[edge], activeDraft.zone);
      rebased[edge] = ms === null ? activeDraft.form[edge] : msToZonedText(ms, zone.name);
    }
    setDraft({ seed: segment, zone: zone.name, form: rebased });
  };

  // `report` exists because one control does not sit near the shared error line: the
  // unarchive chip is at the top of a panel that scrolls, and a refusal printed a screen
  // further down is a refusal nobody reads.
  // Typed as `Run` at the definition rather than inferred: `setError` as a default would
  // otherwise widen `report` to React's updater form, which is not what a panel may pass.
  const run: Run = async (work, report = setError) => {
    report(null);
    try {
      await work();
    } catch (caught) {
      // A 409 here is the exclusion constraint refusing overlapping shows, which is the
      // most likely outcome of a careless drag and deserves to be readable.
      report(caught instanceof ApiError ? caught.detail : String(caught));
    }
  };

  const save = () =>
    run(async () => {
      if (!timesValid || conflict) return;
      // Only the visible fields go either way. Sending a track's blank show fields would ask
      // the server to null out columns the operator was never shown.
      const before = {
        start: segment.start,
        end: segment.end,
        ...Object.fromEntries(fields.map((name) => [name, segment[name]])),
      };
      await update.mutateAsync({
        id: segment.id,
        patch: {
          ...edges,
          ...Object.fromEntries(fields.map((name) => [name, form[name].trim() || null])),
        },
      });
      setDraft(null);
      useUndo.getState().push({
        label: t("inspector.undoEdit", { id: segment.id }),
        run: () => update.mutateAsync({ id: segment.id, patch: before }),
      });
    });

  return (
    <Paper
      component="aside"
      p={14}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <Group component="header" justify="space-between" wrap="nowrap" mb={12}>
        <Title order={3} fz={15} lh={1.5}>
          #{segment.id}{" "}
          <Text component="span" size="xs" fw={400} c="dimmed">
            {t(`inspector.kind.${segment.kind}`, { defaultValue: segment.kind })}
          </Text>
        </Title>
        <StateBadge segment={segment} />
      </Group>

      {exported && <ExportedNotice segment={segment} run={run} />}

      <MetaFields segment={segment} form={form} setForm={setForm} />

      <SegmentTimes
        segment={segment}
        form={form}
        setForm={setForm}
        zone={zone}
        startMs={startMs}
        endMs={endMs}
      />

      {conflict && (
        <Alert color="yellow" variant="light" mb={10}>
          <Text size="sm">{t("inspector.serverChanged")}</Text>
          <Group gap={6} mt={8}>
            <Chip onClick={useServer}>{t("inspector.useServerVersion")}</Chip>
            <Chip variant="accent" onClick={keepDraft}>
              {t("inspector.keepDraft")}
            </Chip>
          </Group>
        </Alert>
      )}

      <FeedbackLine role="alert">{error}</FeedbackLine>

      <ReviewChips
        segment={segment}
        run={run}
        dirty={dirty && !conflict}
        timesValid={timesValid}
        saving={update.isPending}
        onSave={() => void save()}
      />

      <StructureChips segment={segment} run={run} timesValid={timesValid} report={setError} />

      <ExcisionsNote segment={segment} />

      <PublishPanel segment={segment} run={run} />

      <SplitProposal segment={segment} run={run} />

      <ArtworkPanel segment={segment} run={run} defaultCover={defaultCover} />

      <FactsGrid segment={segment} zone={zone.name} />
    </Paper>
  );
}
